import React, { useEffect, useState } from 'react';
import { useLocation, Outlet } from 'react-router-dom';

import styled from 'styled-components';
import Link from '@joinforma/ui/components/legacy/Link';
import { SVG_ICON_COLORS } from '@joinforma/ui/constants/properties';
import Headline2 from '@joinforma/ui/components/Headline2';
import Body1 from '@joinforma/ui/components/Body1';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import { ReactComponent as LockSvg } from '@joinforma/ui/assets/icons/lock.svg';
import { ReactComponent as FormaLogo } from 'assets/images/logo-colored-with-name.svg';
import { SCREEN_WIDTHS } from '@joinforma/ui/constants/breakpoints';

import { WelcomeRoutes } from 'utils/routeGenerator';

const Container = styled.div`
  position: absolute;
  background: linear-gradient(180deg, #7b77ff 0%, #feeaea 79.74%),
    radial-gradient(
      172.36% 172.36% at 50% -34.62%,
      #e0dfff 3.59%,
      #feeaea 36.5%,
      #e0dfff 57.88%,
      #7b77ff 96.57%
    );
  width: 100%;
  z-index: 1000;
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    height: 100vh;
    overflow-y: hidden;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  overflow-y: hidden;
  overflow-x: auto;
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    gap: 120px;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
  }
`;

const AngledFooterRight = styled.div`
  display: none;
  position: absolute;
  right: 0;
  bottom: 0;
  height: 35%;
  width: 50%;
  background-color: white;
  z-index: 0;
  content: '';
  transform: skewY(-18.5deg);
  transform-origin: 100%;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: block;
  }
`;

const AngledFooterLeft = styled.div`
  display: none;
  position: absolute;
  left: 0;
  bottom: 0;
  height: 35%;
  width: 50%;
  background-color: white;
  z-index: 0;
  content: '';
  transform: skewY(18.5deg);
  transform-origin: 0%;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: block;
  }
`;

const WelcomeInfo = styled.div`
  position: static;
  padding-top: 0;
  display: flex;
  margin-bottom: 40px;
  flex-direction: column;
  align-items: center;
  width: 344px;
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    align-items: flex-start;
    margin-bottom: 0;
    padding-top: 170px;
  }
`;

const WelcomeTextContainer = styled.div`
  display: none;
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    display: block;
  }
`;

const WelcomeColumn = styled.div`
  color: ${({
    theme: {
      colors: {
        text: { light },
      },
    },
  }) => light};
  z-index: 1;
  display: none;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: flex;
    margin-top: 80px;
  }
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    flex-direction: column;
    justify-content: space-between;
    margin-top: 0;
  }
`;

const StyledLogo = styled(FormaLogo)`
  width: 160px;
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    margin-bottom: 60px;
  }
`;

const Footer = styled.div`
  display: none;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: flex;
    align-items: center;
  }
  margin: auto;
  margin-top: 15px;
  margin-bottom: 15px;
`;

const Divider = styled.div`
  padding-left: 5px;
  padding-right: 5px;
`;

const WelcomeHeader = styled(Headline2)`
  color: ${({
    theme: {
      colors: {
        text: { light },
      },
    },
  }) => light};
  margin-bottom: 16px;
`;
const WelcomeText = styled(Body1)`
  display: block;
  color: ${({
    theme: {
      colors: {
        text: { light },
      },
    },
  }) => light};
  line-height: 28px;
`;

const ScrollContainer = styled.div`
  overflow-x: hidden;
  ::-webkit-scrollbar {
    display: none;
  }
  z-index: 1;
  width: 100%;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    width: inherit;
  }
  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    height: 100vh;
    overflow-y: scroll;
    padding: 120px 13px 0 13px;
  }
`;

const InformationContainer = styled.div`
  width: 100%;
  height: fit-content;
  position: relative;
  border-radius: 7px;
  padding: 16px;
  background: ${({
    theme: {
      colors: {
        background: { primary },
      },
    },
  }) => primary};
  box-shadow: 0px 4px 12px 2px rgba(0, 0, 0, 0.08);
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    margin-bottom: 40px;
  }
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    width: 724px;
    padding: 10px 80px 0px 80px;
  }
`;

const EncryptContainer = styled.div`
  display: none;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    margin-right: 390px;
  }
`;
const IconContainer = styled.div``;
const EncryptText = styled.div`
  margin-top: 3px;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const StyledLockIcon = styled(SvgIcon).attrs({
  icon: LockSvg,
  stroke: SVG_ICON_COLORS.SECONDARY,
  fill: SVG_ICON_COLORS.LIGHT,
})`
  padding-top: 4px;
`;

const WelcomePage = () => {
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState();

  useEffect(() => {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  return (
    <Container>
      <ContentContainer>
        <WelcomeColumn>
          <WelcomeInfo>
            <StyledLogo />
            <WelcomeTextContainer>
              <WelcomeHeader>Welcome to Forma!</WelcomeHeader>
              <WelcomeText>
                We give you the flexibility to spend your benefits how you want.
                Manage life and work seamlessly.
              </WelcomeText>
            </WelcomeTextContainer>
          </WelcomeInfo>
        </WelcomeColumn>

        <ScrollContainer
          ref={(el) =>
            el ? el.scrollTo({ top: 0, behavior: 'smooth' }) : null
          }
        >
          <InformationContainer>
            <Outlet />
          </InformationContainer>
          <Footer>
            Forma Inc <Divider>&#8226;</Divider>
            <Link
              target="_blank"
              href="https://www.joinforma.com/legal/privacy-policy"
            >
              Privacy
            </Link>
            <Divider>&</Divider>
            <Link
              target="_blank"
              href="https://www.joinforma.com/legal/user-terms"
            >
              Terms
            </Link>
          </Footer>
          {currentPath === WelcomeRoutes.finishAccount() && (
            <EncryptContainer>
              <IconContainer>
                <StyledLockIcon />
              </IconContainer>
              <EncryptText>
                Your Information is protected with 128-bit SSL encryption.
              </EncryptText>
            </EncryptContainer>
          )}
        </ScrollContainer>
      </ContentContainer>

      <AngledFooterRight />
      <AngledFooterLeft />
    </Container>
  );
};

export default WelcomePage;
